/**
 * Studio settings — the studio's own identity, and the only screen that
 * changes how every report looks.
 *
 * Two things live here that nothing else in the product touches: the logo that
 * heads each page of a report, and the palette those pages are printed in.
 * Both end up on a document handed to a paying client, so both sit behind the
 * studio password (PRD §3.3) and the palette is chosen from ten curated sets
 * rather than typed as a colour — an owner picking three colours freehand
 * produces a report that reads as amateur, and the palettes exist so that
 * cannot happen (Doc 6 §3).
 *
 * The swatches are drawn from the palettes the API sends, not from a copy kept
 * here, so what an owner previews is what their report is printed in.
 */

import { useEffect, useRef, useState } from 'react';
import { CornerOrnament } from '@pp/report-template';
import { MIN_PASSWORD_LENGTH, getOrnamentKey } from '@pp/shared';
import { ApiError, api, request } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { AdminReauthDialog } from '../components/ui/AdminReauthDialog.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { Field } from '../components/ui/Field.js';
import { useApiData } from '../hooks/useApiData.js';
import { useToast } from '../components/ui/ToastContext.js';
import { useSession } from '../session/SessionContext.js';
import './StudioSettings.css';

interface PaletteOption {
  key: string;
  name: string;
  tokens: {
    bg: string;
    surface: string;
    ink: string;
    primary: string;
    accent: string;
    border: string;
  };
}

interface OrnamentOption {
  key: string;
  name: string;
  description: string;
}

interface StudioSettingsData {
  id: string;
  name: string;
  phone: string | null;
  instagram: string | null;
  website: string | null;
  email: string | null;
  paletteKey: string;
  ornamentKey: string;
  skipProfilePassword: boolean;
  logoUrl: string | null;
  palettes: PaletteOption[];
  ornaments: OrnamentOption[];
}

interface FormState {
  name: string;
  phone: string;
  instagram: string;
  website: string;
  email: string;
  paletteKey: string;
  ornamentKey: string;
  skipProfilePassword: boolean;
}

/**
 * The real corner motif, at the corner of a page-shaped box.
 *
 * Drawn by the report template's own component so the picker cannot show one
 * shape and the report print another. The two opacity rules the report applies
 * to `__leaves` and `__stems` live in the report stylesheet, which this screen
 * already loads, so the preview is tinted exactly as the page will be.
 */
function OrnamentPreview({
  ornamentKey,
  accent,
}: {
  ornamentKey: string;
  accent: string | undefined;
}) {
  return (
    <span
      className="settings__ornament-art"
      style={accent ? ({ ['--pp-accent']: accent } as React.CSSProperties) : undefined}
    >
      <CornerOrnament ornamentKey={getOrnamentKey(ornamentKey)} />
    </span>
  );
}

/** 2 MB: a logo is a mark, not a photograph. */
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export function StudioSettings() {
  const { session, hasAdminAccess, refresh } = useSession();
  const toast = useToast();
  const { data, error, loading, reload } = useApiData<StudioSettingsData>('/api/studio');

  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string>();
  const [reauthFor, setReauthFor] = useState<null | (() => void)>(null);
  const [confirmLogoDelete, setConfirmLogoDelete] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string>();
  const [passwordBusy, setPasswordBusy] = useState(false);

  /*
   * Filled once, from the first response — not every time `data` changes.
   *
   * `reload()` runs after a logo upload, and its fetch can resolve while the
   * owner is mid-edit on another field: they upload a logo, start typing a
   * phone number while the reload is still in flight, and if that fetch lands
   * after the keystroke this effect would overwrite what they just typed with
   * whatever was on the server a moment ago — a save that looks successful
   * but silently ships the old value. Found exactly that way, by a script
   * that filled a logo and a field back to back and got the field it had
   * already replaced.
   *
   * Once `form` exists, nothing here should touch it again: the local edit is
   * authoritative until the owner saves or leaves the screen.
   */
  useEffect(() => {
    if (!data || form) return;
    setForm({
      name: data.name,
      phone: data.phone ?? '',
      instagram: data.instagram ?? '',
      website: data.website ?? '',
      email: data.email ?? '',
      paletteKey: data.paletteKey,
      ornamentKey: data.ornamentKey,
      skipProfilePassword: data.skipProfilePassword,
    });
    // form is deliberately not a dependency — see the comment above.
  }, [data]);

  if (session && session.role !== 'owner') {
    return (
      <section className="settings">
        <h1 className="page__title">Stüdyo Ayarları</h1>
        <p className="instructors__denied">
          Stüdyo ayarları yalnızca salon yöneticisi tarafından değiştirilir.
        </p>
      </section>
    );
  }

  /** Every write here needs the studio password within the last ten minutes. */
  function withAdmin(action: () => void) {
    if (hasAdminAccess()) action();
    else setReauthFor(() => action);
  }

  async function save() {
    if (!form) return;
    setBusy(true);
    setFormError(undefined);

    try {
      await api.patch('/api/studio', {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        instagram: form.instagram.trim() || null,
        website: form.website.trim() || null,
        email: form.email.trim() || null,
        paletteKey: form.paletteKey,
        ornamentKey: form.ornamentKey,
        skipProfilePassword: form.skipProfilePassword,
      });
      toast.show('Ayarlar kaydedildi.');
      reload();
      // The studio name and palette are shown by the shell and used by the
      // report preview, so the session has to hear about the change too.
      void refresh();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function uploadLogo(file: File) {
    if (file.size > MAX_LOGO_BYTES) {
      setFormError('Logo dosyası en fazla 2 MB olabilir.');
      return;
    }

    setBusy(true);
    setFormError(undefined);

    try {
      const body = new FormData();
      body.append('image', file);
      await request('/api/studio/logo', { method: 'POST', formData: body });
      toast.show('Logo yüklendi.');
      reload();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function removeLogo() {
    setConfirmLogoDelete(false);
    setBusy(true);
    try {
      await api.delete('/api/studio/logo');
      toast.show('Logo kaldırıldı.');
      reload();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    } finally {
      setBusy(false);
    }
  }

  /**
   * No admin re-auth dialog here — the current password the form already
   * asks for is its own proof, and asking for the studio password twice in a
   * row would just mean typing the same secret two times.
   */
  async function changePassword(event: React.FormEvent) {
    event.preventDefault();
    setPasswordBusy(true);
    setPasswordError(undefined);

    try {
      await api.post('/api/studio/change-password', { currentPassword, newPassword });
      toast.show('Şifreniz değiştirildi.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setPasswordError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    } finally {
      setPasswordBusy(false);
    }
  }

  const chosen = data?.palettes.find((palette) => palette.key === form?.paletteKey);

  return (
    <section className="settings">
      <h1 className="page__title">Stüdyo Ayarları</h1>
      <p className="settings__hint">
        Buradaki her şey öğrenciye giden rapora yansır: salon adı, iletişim bilgileri,
        logo ve raporun rengi.
      </p>

      {(error || formError) && <p role="alert">{error ?? formError}</p>}
      {loading && <p className="profile__muted">Yükleniyor…</p>}

      {form && data && (
        <>
          {/* ------------------------------------------------------- logo */}
          <div className="settings__block">
            <h2 className="settings__heading">Logo</h2>
            <p className="settings__note">
              Raporun her sayfasının üst kısmında görünür. Şeffaf zeminli PNG en iyi
              sonucu verir.
            </p>

            <div className="settings__logo">
              <div className="settings__logo-frame">
                {data.logoUrl ? (
                  <img src={data.logoUrl} alt={`${data.name} logosu`} />
                ) : (
                  <span className="settings__logo-empty">Logo yok</span>
                )}
              </div>

              <div className="settings__logo-actions">
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="settings__file"
                  id="settings-logo"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) withAdmin(() => void uploadLogo(file));
                  }}
                />
                <label className="settings__file-label" htmlFor="settings-logo">
                  {data.logoUrl ? 'Logoyu Değiştir' : 'Logo Yükle'}
                </label>

                {data.logoUrl && (
                  <button
                    type="button"
                    className="settings__remove"
                    onClick={() => setConfirmLogoDelete(true)}
                  >
                    Kaldır
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ---------------------------------------------------- palette */}
          <div className="settings__block">
            <h2 className="settings__heading">Rapor Rengi</h2>
            <p className="settings__note">
              Raporun tamamı seçtiğiniz renk ailesiyle basılır. Aşağıdaki kutucuklar
              gerçek renklerdir.
            </p>

            <ul className="settings__palettes">
              {data.palettes.map((palette) => (
                <li key={palette.key}>
                  <button
                    type="button"
                    className={
                      palette.key === form.paletteKey
                        ? 'settings__palette settings__palette--on'
                        : 'settings__palette'
                    }
                    aria-pressed={palette.key === form.paletteKey}
                    onClick={() => setForm({ ...form, paletteKey: palette.key })}
                  >
                    <span
                      className="settings__swatch"
                      style={{
                        background: palette.tokens.bg,
                        borderColor: palette.tokens.border,
                      }}
                    >
                      <span style={{ background: palette.tokens.primary }} />
                      <span style={{ background: palette.tokens.accent }} />
                      <span style={{ background: palette.tokens.surface }} />
                    </span>
                    <span className="settings__palette-name">{palette.name}</span>
                  </button>
                </li>
              ))}
            </ul>

            {/* A line of the report, in the palette being considered. Cheaper
                than opening the wizard to find out what a colour looks like. */}
            {chosen && (
              <div
                className="settings__preview"
                style={{
                  background: chosen.tokens.bg,
                  borderColor: chosen.tokens.border,
                  color: chosen.tokens.ink,
                }}
              >
                <span
                  className="settings__preview-title"
                  style={{ color: chosen.tokens.primary }}
                >
                  Gelişim Raporu
                </span>
                <span
                  className="settings__preview-rule"
                  style={{ background: chosen.tokens.accent }}
                />
                <span className="settings__preview-name">{form.name || data.name}</span>
              </div>
            )}
          </div>

          {/* ----------------------------------------------------- ornament */}
          <div className="settings__block">
            <h2 className="settings__heading">Köşe Motifi</h2>
            <p className="settings__note">
              Rapor sayfalarının sağ üst köşesinde görünen süs. Seçtiğiniz rapor rengiyle
              boyanır.
            </p>

            <ul className="settings__ornaments">
              {data.ornaments.map((ornament) => (
                <li key={ornament.key}>
                  <button
                    type="button"
                    className={
                      ornament.key === form.ornamentKey
                        ? 'settings__ornament settings__ornament--on'
                        : 'settings__ornament'
                    }
                    aria-pressed={ornament.key === form.ornamentKey}
                    onClick={() => setForm({ ...form, ornamentKey: ornament.key })}
                  >
                    {/* The motif itself, drawn at the corner of a page shape —
                        a name alone would tell an owner nothing. */}
                    <span
                      className="settings__ornament-page"
                      style={{
                        background: chosen?.tokens.bg,
                        borderColor: chosen?.tokens.border,
                        color: chosen?.tokens.primary,
                      }}
                    >
                      <OrnamentPreview
                        ornamentKey={ornament.key}
                        accent={chosen?.tokens.accent}
                      />
                    </span>
                    <span className="settings__ornament-text">
                      <span className="settings__ornament-name">{ornament.name}</span>
                      <span className="settings__ornament-desc">
                        {ornament.description}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* ------------------------------------------------------ fields */}
          <div className="settings__block">
            <h2 className="settings__heading">Salon Bilgileri</h2>
            <p className="settings__note">
              Doldurduğunuz iletişim bilgileri raporun alt bilgisinde görünür. Boş
              bıraktıklarınız hiç yazılmaz.
            </p>

            <div className="settings__fields">
              <Field
                label="Salon Adı"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
              <Field
                label="Telefon"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <Field
                label="Instagram"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                hint="Örnek: @lunapilates"
                value={form.instagram}
                onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                insertable={['@']}
                onInsert={(value) => setForm({ ...form, instagram: value })}
              />
              <Field
                label="Web Sitesi"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
              <Field
                label="E-posta"
                type="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                hint="@ tuşuna basamıyorsanız aşağıdaki düğmeyi kullanın."
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                insertable={['@']}
                onInsert={(value) => setForm({ ...form, email: value })}
              />
            </div>
          </div>

          {/* ---------------------------------------------------- picker */}
          <div className="settings__block">
            <h2 className="settings__heading">Kolay Giriş</h2>
            <p className="settings__note">
              Açarsanız, salon şifresiyle giriş yaptıktan sonra bir eğitmen profili
              seçerken veya yönetici olarak devam ederken ikinci bir şifre sorulmaz. Salon
              kodu ve girişteki ilk şifre her zaman istenmeye devam eder — bu yalnızca
              giriş yaptıktan sonraki adımı kısaltır.
            </p>

            <label className="settings__checkbox">
              <input
                type="checkbox"
                checked={form.skipProfilePassword}
                onChange={(e) =>
                  setForm({ ...form, skipProfilePassword: e.target.checked })
                }
              />
              Profil seçerken ve yönetici moduna girerken şifre sorulmasın
            </label>
          </div>

          <div className="settings__actions">
            <Button
              type="button"
              loading={busy}
              disabled={form.name.trim() === ''}
              onClick={() => withAdmin(() => void save())}
            >
              Kaydet
            </Button>
          </div>

          {/* --------------------------------------------------- password */}
          <div className="settings__block">
            <h2 className="settings__heading">Şifre Değiştir</h2>
            <p className="settings__note">
              Girişte kullandığınız salon şifresi. Değiştirdiğinizde, bu cihaz dışındaki
              açık oturumlar kapanır.
            </p>

            <form onSubmit={changePassword} className="settings__fields" noValidate>
              <Field
                label="Mevcut Şifre"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                error={passwordError}
                required
              />
              <Field
                label="Yeni Şifre"
                type="password"
                autoComplete="new-password"
                hint={`En az ${MIN_PASSWORD_LENGTH} karakter.`}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </form>

            <div className="settings__actions">
              <Button
                type="button"
                loading={passwordBusy}
                disabled={
                  currentPassword === '' || newPassword.length < MIN_PASSWORD_LENGTH
                }
                onClick={(event) => void changePassword(event)}
              >
                Şifreyi Değiştir
              </Button>
            </div>
          </div>
        </>
      )}

      {confirmLogoDelete && (
        <ConfirmDialog
          title="Logo kaldırılsın mı?"
          body="Bundan sonra üretilen raporlarda logo yerine salon adı yazar. Daha önce oluşturulmuş raporlar değişmez."
          confirmLabel="Kaldır"
          destructive
          onCancel={() => setConfirmLogoDelete(false)}
          onConfirm={() => {
            setConfirmLogoDelete(false);
            withAdmin(() => void removeLogo());
          }}
        />
      )}

      {reauthFor && (
        <AdminReauthDialog
          action="Stüdyo ayarlarını değiştirmek"
          onCancel={() => setReauthFor(null)}
          onConfirmed={() => {
            const action = reauthFor;
            setReauthFor(null);
            action();
          }}
        />
      )}
    </section>
  );
}
